import axios from 'axios';
import { debug, error } from '@/utils/logger';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';
import Save from '@/models/save';
import Post from '@/models/post';

// const API_URL = 'http://localhost:8080';
const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
const SAVE_POST = `${API_URL}/api/v2/save`;
const UNSAVE_POST = `${API_URL}/api/v2/unsave`;
const PIN_POST_BASE = `${API_URL}/api/v1/post`;

export async function savePost(postId: string): Promise<Save> {
    try {
        debug('Saving post with data:', SAVE_POST, {postId});
        const body: Record<string, string> = { postId };
        const referralCreatorId = getReferralCreatorIdForApi();
        if (referralCreatorId) body.referral_creator_id = referralCreatorId;
        const response = await axios.post(`${SAVE_POST}`, body);
        debug('Response:', response.data);
        return new Save(response.data);
    } catch (err) {
        error('Error saving post:', err);
        throw err;
    }
}

export async function unsavePost(postId: string): Promise<void> {
    try {
        debug('Unsaving post with data:', UNSAVE_POST, {postId});
        const response = await axios.post(`${UNSAVE_POST}`, {postId});
        debug('Response:', response.data);
        // Unsave returns acknowledgement, not a model
    } catch (err) {
        error('Error unsaving post:', err);
        throw err;
    }
}

/**
 * Pin or unpin a post to/from the user's profile
 * @param postId - The ID of the post to pin/unpin
 * @param isPinned - true to pin, false to unpin
 * @returns Updated post object
 * @throws Error if user doesn't own the post (403) or post not found (404)
 */
export async function pinPost(postId: string, isPinned: boolean): Promise<Post> {
    try {
        debug('[pinPost] Pinning/unpinning post', { postId, isPinned });
        
        const response = await axios.patch(
            `${PIN_POST_BASE}/${postId}/pin`,
            { is_pinned: isPinned }
        );

        debug('[pinPost] Successfully pinned/unpinned post', {
            postId,
            isPinned: response.data.is_pinned,
        });

        return new Post(response.data);
    } catch (err) {
        error('[pinPost] Error pinning/unpinning post:', err);
        
        // Handle specific error cases
        if (axios.isAxiosError(err)) {
            if (err.response?.status === 403) {
                throw new Error(err.response.data?.message || 'You can only pin or unpin your own posts');
            }
            if (err.response?.status === 404) {
                throw new Error('Post not found');
            }
            if (err.response?.status === 400) {
                throw new Error('Invalid request');
            }
        }
        
        throw err;
    }
}